/**
 * DiceTryScene — shared base for the local "try the dice" experiences.
 *
 * Owns a fully ISOLATED 3D scene (its own GameScene + DiceController +
 * CameraController) with no game-server socket, exactly like TutorialController
 * Module 1: the player picks up and throws dice to see how they feel. Subclasses
 * add their own top control + decide which skins to spawn:
 *   - OfferDiceTryController  → an offer's dice (drag-palette chooser)
 *   - EquippedDiceTryController → the player's equipped loadout (2/3 dice toggle)
 *
 * start() builds the scene + runs the engine loop; close (X) → exit() tears it
 * all down (disposes physics, stops the engine, wipes the scene) and restores
 * the caller via onExit, mirroring the tutorial teardown.
 */

import { Engine } from '../../engine/Engine';
import { DiceController } from '../dice/DiceController';
import { CameraController } from '../camera/CameraController';
import { GameScene } from '../scene/GameScene';
import { SceneManager } from '../scene/SceneManager';
import { DiceAssetManager } from '../dice/assets/DiceAssetManager';
import type { GameModeId } from '@sdl/scene-format';

const STYLE_ID = 'sdl-dicetry-styles';

export interface DiceTrySceneDeps {
  engine: Engine;
  diceAssetManager: DiceAssetManager | null;
  cdnBase: string | null;
  /** Restore the caller (re-show the home UI / previous screen). */
  onExit: () => void;
}

/** Centred rest positions for N demo dice (toward the player). */
function restPositions(count: number): Array<{ x: number; y: number; z: number }> {
  const gap = 1.5;
  const startX = -((count - 1) * gap) / 2;
  return Array.from({ length: count }, (_, i) => ({ x: startX + i * gap, y: 1, z: 4 }));
}

export abstract class DiceTryScene {
  protected engine: Engine;
  protected diceAssetManager: DiceAssetManager | null;
  protected cdnBase: string | null;
  private onExitCb: () => void;

  protected scene: GameScene | null = null;
  protected dice: DiceController | null = null;
  protected camera: CameraController | null = null;
  private closeBtn: HTMLButtonElement | null = null;
  private unsubUpdate: (() => void) | null = null;
  protected active = false;

  constructor(deps: DiceTrySceneDeps) {
    this.engine = deps.engine;
    this.diceAssetManager = deps.diceAssetManager;
    this.cdnBase = deps.cdnBase;
    this.onExitCb = deps.onExit;
  }

  /** Subclasses build their control + spawn the first dice here. */
  abstract start(): Promise<void>;

  /** Which editor game-mode scene assignment this try experience loads.
   *  'freeroll' = the Dice screen TRY (own dice); the offer demo overrides to
   *  'offer-dice'. Unassigned modes fall back to the default scene server-side. */
  protected readonly sceneGameMode: GameModeId = 'freeroll';

  /** Preload skins, raced with a timeout so a slow CDN can't stall the demo. */
  protected async preload(codes: string[]): Promise<void> {
    const ids = codes.filter(Boolean);
    if (this.diceAssetManager && ids.length > 0) {
      await Promise.race([
        this.diceAssetManager.preload(ids),
        new Promise((resolve) => setTimeout(resolve, 5000)),
      ]);
    }
  }

  protected async buildScene(): Promise<void> {
    // Reuse the resident environment (built once, kept warm by SceneManager).
    // The try modes (freeroll / offer-dice) are editor-assignable; when they
    // point at the same scene id as the match modes (the common case) this
    // never rebuilds — it just hands back the already-loaded scene. A distinct
    // assignment rebuilds the resident scene once on entry, by design.
    this.scene = await SceneManager.instance.ensureScene(this.sceneGameMode);

    // The resident scene is shared: a prior match's betting phase can leave the
    // anchored lights at the betting anchor. This is a shooting-only experience,
    // so reset them to the captured shooting (base) pose on entry. Without this
    // the dice show up lit for the betting camera, not the shooting view.
    this.scene.applyShootingLights();

    this.dice = new DiceController(this.engine, {
      diceCount: 3,
      energyGain: 0.3,
      maxEnergy: 100,
      minEnergyToThrow: 18, // match the real game's throw gate
      maxPower: 35,
      baseForward: 5,
      holdHeight: 4,
      pickupRadius: 1.5,
    });
    if (this.diceAssetManager) this.dice.setAssetManager(this.diceAssetManager);

    // The camera is a shared, session-wide resource (one engine.camera). Use the
    // single CameraController owned by SceneManager instead of newing our own.
    // Snap it to the shooting (default) pose now: it may have been left at the
    // betting anchor by a prior match, and this is a shooting-only experience.
    this.camera = SceneManager.instance.cameraController;
    this.camera.reset();

    this.unsubUpdate = this.engine.onUpdate((dt) => {
      this.dice?.update(dt);
      this.camera?.update(dt);
      this.scene?.update(dt);
    });

    this.dice.on('dice:pickup', () => { this.camera?.setMode('zoom_unheld'); this.onDiceHandling(); });
    this.dice.on('dice:all_collected', () => this.camera?.setMode('default'));
    this.dice.on('dice:rolling', () => { this.camera?.setMode('follow'); this.onDiceHandling(); });
    this.dice.on('dice:stopped', () => this.camera?.setMode('zoom'));
  }

  /** (Re)create the demo dice at rest with the given skins (count = skins.length). */
  protected spawnDice(skins: string[]): void {
    if (!this.dice || !this.camera || skins.length === 0) return;
    this.dice.setEquippedDice(skins);
    this.dice.clearDice();
    this.dice.createDice(restPositions(skins.length));
    this.camera.setDice(this.dice.getDice());
    this.dice.setEnabled(true);
    this.camera.setMode('default');
  }

  protected buildCloseButton(): void {
    DiceTryScene.injectStyles();
    const btn = document.createElement('button');
    btn.className = 'sdl-dicetry-close';
    btn.setAttribute('aria-label', 'Close');
    btn.innerHTML = '&times;';
    btn.addEventListener('click', () => this.exit());
    document.body.appendChild(btn);
    this.closeBtn = btn;
  }

  protected exit(): void {
    if (!this.active) return;
    this.active = false;
    this.teardown();
    this.onExitCb();
  }

  private teardown(): void {
    this.disposeControls();
    this.closeBtn?.remove();
    this.closeBtn = null;
    this.unsubUpdate?.();
    this.unsubUpdate = null;
    // Dispose only the ACTORS we added (dice meshes + bodies via Dice.dispose,
    // camera). The environment is owned by SceneManager and stays resident —
    // disposing it / clearing engine.scene / physics.dispose() is exactly the
    // churn that blacked out the iOS webview on repeated try-dice cycles.
    this.dice?.dispose();
    this.dice = null;
    this.camera?.reset();
    this.camera = null;
    // Safety net: detach any leftover actor object, keeping ONLY the resident
    // environment (mirrors the main game's cleanup). Never clears the scene.
    const keep = new Set(SceneManager.instance.current?.getPersistentObjects() ?? []);
    for (const child of [...this.engine.scene.children]) {
      if (!keep.has(child)) this.engine.scene.remove(child);
    }
    this.scene = null;  // drop the reference only; SceneManager owns the scene
    this.engine.stop();
  }

  destroy(): void {
    if (this.active) {
      this.active = false;
      this.teardown();
    }
  }

  protected thumb(id: string): string {
    return `${this.cdnBase ?? ''}/dice/${id}/thumb-128.png`;
  }

  // ---- subclass hooks ----------------------------------------------------

  /** Fired when dice are picked up / start rolling (e.g. collapse a chooser). */
  protected onDiceHandling(): void { /* default: nothing */ }
  /** Tear down subclass-specific UI during teardown. */
  protected disposeControls(): void { /* default: nothing */ }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-dicetry-close {
        position: fixed; z-index: 215;
        left: 50%; bottom: calc(env(safe-area-inset-bottom, 0px) + 32px);
        transform: translateX(-50%);
        width: 42px; height: 42px;
        background: rgba(10, 10, 14, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.18);
        border-radius: 50%;
        color: rgba(255, 255, 255, 0.85);
        font-size: 26px; line-height: 1;
        cursor: pointer; -webkit-tap-highlight-color: transparent;
        box-shadow: 0 8px 22px rgba(0, 0, 0, 0.55);
        transition: background 0.2s, color 0.2s, transform 0.12s;
      }
      .sdl-dicetry-close:active { transform: translateX(-50%) scale(0.92); background: rgba(255, 255, 255, 0.15); color: #fff; }
    `;
    document.head.appendChild(style);
  }
}
